import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// --- Token storage (localStorage) ---
// We store tokens locally AND use HttpOnly cookies. Bearer tokens work
// reliably across any domain / ingress / cookie policy; cookies provide
// defense-in-depth for same-origin deployments.
const ACCESS_KEY = "ondina_access_token";
const REFRESH_KEY = "ondina_refresh_token";
const safeStorage = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};
export const getAccessToken = () => safeStorage.get(ACCESS_KEY);
export const setTokens = (access, refresh) => {
  if (access) safeStorage.set(ACCESS_KEY, access);
  if (refresh) safeStorage.set(REFRESH_KEY, refresh);
};
export const clearTokens = () => {
  safeStorage.del(ACCESS_KEY);
  safeStorage.del(REFRESH_KEY);
};

// Attach Bearer token to every request (if present)
api.interceptors.request.use((config) => {
  const t = safeStorage.get(ACCESS_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// --- 401 handling: transparent refresh + retry, then fallback to /login ---
let refreshPromise = null;
const AUTH_PATHS = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/me"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { response, config } = error;
    if (!response || !config) return Promise.reject(error);
    const reqPath = (config.url || "").replace(api.defaults.baseURL, "");
    const isAuthCall = AUTH_PATHS.some((p) => reqPath.startsWith(p));
    if (response.status !== 401 || isAuthCall || config._retry) {
      return Promise.reject(error);
    }
    try {
      if (!refreshPromise) {
        const refreshToken = safeStorage.get(REFRESH_KEY);
        refreshPromise = axios
          .post(`${API}/auth/refresh`, refreshToken ? { refresh_token: refreshToken } : null, {
            withCredentials: true,
          })
          .then((r) => {
            if (r?.data?.access_token) safeStorage.set(ACCESS_KEY, r.data.access_token);
            return r;
          })
          .finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      config._retry = true;
      return api.request(config);
    } catch {
      // Refresh failed → session really expired. Bounce to login (preserve return URL).
      clearTokens();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login") && !window.location.pathname.startsWith("/bill/")) {
        const back = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(back)}`;
      }
      return Promise.reject(error);
    }
  },
);

// ---------- Auth ----------
export const login = (email, password) =>
  api.post("/auth/login", { email, password }).then(r => {
    const { access_token, refresh_token, ...user } = r.data || {};
    if (access_token || refresh_token) setTokens(access_token, refresh_token);
    return user;
  });
export const logout = () =>
  api.post("/auth/logout").then(r => r.data).finally(() => clearTokens());
export const getMe = () => api.get("/auth/me").then(r => r.data);

// ---------- Seed ----------
export const seedData = () => api.post("/seed").then(r => r.data);

// ---------- Partners / Retreats ----------
export const getPartners = () => api.get("/partners").then(r => r.data);
export const createPartner = (data) => api.post("/partners", data).then(r => r.data);
export const updatePartner = (id, data) => api.patch(`/partners/${id}`, data).then(r => r.data);
export const deletePartner = (id, cascade = false) =>
  api.delete(`/partners/${id}`, { params: cascade ? { cascade: true } : {} }).then(r => r.data);

export const getRetreats = (params = {}) => api.get("/retreats", { params }).then(r => r.data);
export const getRetreat = (id) => api.get(`/retreats/${id}`).then(r => r.data);
export const createRetreat = (data) => api.post("/retreats", data).then(r => r.data);
export const updateRetreat = (id, data) => api.patch(`/retreats/${id}`, data).then(r => r.data);
export const deleteRetreat = (id) => api.delete(`/retreats/${id}`).then(r => r.data);

export const createRetreatCost = (data) => api.post("/retreat-costs", data).then(r => r.data);
export const updateRetreatCost = (id, data) => api.patch(`/retreat-costs/${id}`, data).then(r => r.data);
export const deleteRetreatCost = (id) => api.delete(`/retreat-costs/${id}`).then(r => r.data);

export const createRetreatPayment = (data) => api.post("/retreat-payments", data).then(r => r.data);
export const updateRetreatPayment = (id, data) => api.patch(`/retreat-payments/${id}`, data).then(r => r.data);
export const deleteRetreatPayment = (id) => api.delete(`/retreat-payments/${id}`).then(r => r.data);

export const getPartnersDashboardSummary = () => api.get("/dashboard/partners-summary").then(r => r.data);

// ---------- Units ----------
export const getUnits = () => api.get("/units").then(r => r.data);
export const createUnit = (data) => api.post("/units", data).then(r => r.data);
export const updateUnit = (id, data) => api.patch(`/units/${id}`, data).then(r => r.data);
export const deleteUnit = (id) => api.delete(`/units/${id}`).then(r => r.data);

// ---------- Products ----------
export const getProducts = (category) =>
  api.get("/products", { params: category ? { category } : {} }).then(r => r.data);
export const createProduct = (data) => api.post("/products", data).then(r => r.data);
export const updateProduct = (id, data) => api.patch(`/products/${id}`, data).then(r => r.data);
export const deleteProduct = (id) => api.delete(`/products/${id}`).then(r => r.data);
export const getProductsSalesSummary = (category) =>
  api.get("/products/sales-summary", { params: category ? { category } : {} }).then(r => r.data);

// ---------- Public config ----------
export const getPublicConfig = () =>
  axios.get(`${API}/public/config`).then(r => r.data);

// ---------- Reservations ----------
export const getReservations = (params = {}) =>
  api.get("/reservations", { params }).then(r => r.data);
export const getReservation = (id) => api.get(`/reservations/${id}`).then(r => r.data);
export const getActiveReservations = () => api.get("/reservations/active").then(r => r.data);
export const createReservation = (data) => api.post("/reservations", data).then(r => r.data);
export const updateReservation = (id, data) => api.patch(`/reservations/${id}`, data).then(r => r.data);
export const deleteReservation = (id) => api.delete(`/reservations/${id}`).then(r => r.data);
export const checkIn = (id) => api.post(`/reservations/${id}/check-in`).then(r => r.data);
export const checkOut = (id) => api.post(`/reservations/${id}/check-out`).then(r => r.data);
export const checkConflict = (params) =>
  api.get("/reservations/check-conflict", { params }).then(r => r.data);

// ---------- Consumptions / Payments ----------
export const addConsumption = (data) => api.post("/consumptions", data).then(r => r.data);
export const addConsumptionsBatch = (data) => api.post("/consumptions/batch", data).then(r => r.data);
export const deleteConsumption = (id) => api.delete(`/consumptions/${id}`).then(r => r.data);
export const addPayment = (data) => api.post("/payments", data).then(r => r.data);
export const deletePayment = (id) => api.delete(`/payments/${id}`).then(r => r.data);

// ---------- Dashboard ----------
export const getOccupancy = (days = 14, start) =>
  api.get("/dashboard/occupancy", { params: { days, ...(start && { start }) } }).then(r => r.data);
export const getTodayActivity = () => api.get("/dashboard/today").then(r => r.data);

// ---------- Revenue ----------
export const getMonthlyRevenue = (year) =>
  api.get("/revenue/monthly", { params: year ? { year } : {} }).then(r => r.data);
export const getRevenueByGuest = (params = {}) =>
  api.get("/revenue/by-guest", { params }).then(r => r.data);
export const getRevenueByPartner = (params = {}) =>
  api.get("/revenue/by-partner", { params }).then(r => r.data);

// ---------- Leads (potential partners pipeline) ----------
export const getLeads = () => api.get("/leads").then(r => r.data.leads);
export const getLead = (id) => api.get(`/leads/${id}`).then(r => r.data);
export const createLead = (data) => api.post("/leads", data).then(r => r.data);
export const updateLead = (id, data) => api.patch(`/leads/${id}`, data).then(r => r.data);
export const deleteLead = (id) => api.delete(`/leads/${id}`).then(r => r.data);
export const convertLead = (id) => api.post(`/leads/${id}/convert`).then(r => r.data);

// ---------- Kitchen ----------
export const getKitchenDietary = () => api.get("/kitchen/dietary").then(r => r.data.guests);
export const getShoppingList = () => api.get("/kitchen/shopping").then(r => r.data.items);
export const createShoppingItem = (data) => api.post("/kitchen/shopping", data).then(r => r.data);
export const updateShoppingItem = (id, data) => api.patch(`/kitchen/shopping/${id}`, data).then(r => r.data);
export const deleteShoppingItem = (id) => api.delete(`/kitchen/shopping/${id}`).then(r => r.data);
export const clearPurchasedShopping = () => api.delete("/kitchen/shopping").then(r => r.data);
export const getKitchenMenus = () => api.get("/kitchen/menus").then(r => r.data.retreats);

// ---------- Retreat Plans (Annual Calendar) ----------
export const getRetreatPlans = (year) =>
  api.get("/retreat-plans", { params: year ? { year } : {} }).then(r => r.data.plans);
export const createRetreatPlan = (data) =>
  api.post("/retreat-plans", data).then(r => r.data);
export const updateRetreatPlan = (id, data) =>
  api.patch(`/retreat-plans/${id}`, data).then(r => r.data);
export const deleteRetreatPlan = (id) =>
  api.delete(`/retreat-plans/${id}`).then(r => r.data);

export const RETREAT_PLAN_STATUSES = [
  { value: "confirmed",    label: "Confirmed",            colorClass: "bg-emerald-500 hover:bg-emerald-600",  ring: "ring-emerald-700" },
  { value: "deposit_paid", label: "Deposit Paid",         colorClass: "bg-blue-500 hover:bg-blue-600",        ring: "ring-blue-700" },
  { value: "interested",   label: "Interested",           colorClass: "bg-amber-400 hover:bg-amber-500 text-amber-950", ring: "ring-amber-600" },
  { value: "closed",       label: "Blocked",              colorClass: "bg-red-500 hover:bg-red-600",          ring: "ring-red-700" },
  { value: "cancelled",    label: "Cancelled",            colorClass: "bg-slate-200 hover:bg-slate-300 text-slate-700", ring: "ring-slate-400" },
];

export const LEAD_STATUSES = [
  { value: "new_inquiry",      label: "New Inquiry" },
  { value: "info_sent",        label: "Info Sent" },
  { value: "waiting_reply",    label: "Waiting Reply" },
  { value: "discussing_terms", label: "Discussing Terms" },
  { value: "interested",       label: "Interested" },
  { value: "deposit_pending",  label: "Deposit Pending" },
  { value: "confirmed",        label: "Confirmed" },
  { value: "lost",             label: "Lost" },
];

// ---------- Public ----------
export const getPublicBill = (id) =>
  axios.get(`${API}/public/bill/${id}`).then(r => r.data);
export const getPublicRetreatBill = (id) =>
  axios.get(`${API}/public/retreat-bill/${id}`).then(r => r.data);

// ---------- CSV download helpers ----------
// Downloads rely on same-site cookies for auth. We avoid JS-based blob downloads
// (which are silently blocked by iframe sandboxes in some hosted preview
// environments) by clicking a regular anchor that points at the /api endpoint —
// the server responds with `Content-Disposition: attachment`, which triggers
// the browser's native download dialog without navigating away.
const buildQuery = (params) => {
  if (!params) return "";
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    q.append(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
};

export const downloadCsv = (path, params, filename) => {
  const url = `${API}${path}${buildQuery(params)}`;
  const a = document.createElement("a");
  a.href = url;
  a.rel = "noopener";
  if (filename) a.download = filename;
  // Keep the current tab alive; if the browser can't auto-download for some
  // reason it will at least show the CSV in a new tab.
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;

// ---------- Date display helpers (European DD/MM/YYYY) ----------
// All stored dates are ISO YYYY-MM-DD (API + HTML <input type="date">).
// These helpers convert the ISO string to user-facing European format.
// They never mutate the underlying value — strictly for display.
export const fmtDate = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
};
export const fmtDateLong = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const d = new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]));
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
export const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return String(iso); }
};

// ---------- Branding ----------
export const LOGO_URL = "https://customer-assets.emergentagent.com/job_quick-checkin-12/artifacts/5k7k7ip3_ondina_logo_web%20%281%29.png";
export const BRAND_NAME = "Ondina Guesthouse";
